import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  DocumentPlusIcon,
  CloudArrowUpIcon,
  UserPlusIcon,
  EyeIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const IncomingInvoices = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [incomingInvoices, setIncomingInvoices] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'list'
  const [partners, setPartners] = useState([]);
  const [filterPartner, setFilterPartner] = useState('');
  const [filterPaymentType, setFilterPaymentType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedFile, setSelectedFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [existingPartner, setExistingPartner] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);
  const [partnerFormData, setPartnerFormData] = useState({
    name: '',
    taxId: '',
    vatTaxId: '',
    address: '',
    city: '',
    country: 'Bulgaria',
    mrp: '',
    bankName: '',
    swiftBic: '',
    iban: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    fetchIncomingInvoices();
    fetchPendingInvoices();
    fetchPartners();
  }, []);

  // Fetch partners for filter dropdown
  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch('/api/finance/partners?activeOnly=true', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch partners');
      }

      const data = await response.json();
      setPartners(data.partners || data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Auto-dismiss messages after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [message.text]);

  const fetchIncomingInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      // Add cache-busting parameter to ensure fresh data
      const response = await fetch(`/api/incoming-invoices?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setIncomingInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Error fetching incoming invoices:', error);
      setMessage({ type: 'error', text: t('incomingInvoices.failedToLoadIncomingInvoices') });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingInvoices = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      // Add cache-busting parameter to ensure fresh data
      const response = await fetch(`/api/incoming-invoices?status=pending_review&t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Error fetching pending invoices:', error);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setSelectedFile(file);

      const formData = new FormData();
      formData.append('invoice', file);

      const token = localStorage.getItem('jwtToken');
      const response = await fetch('/api/incoming-invoices/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setExtractedData(data.extractedData);
        setExistingPartner(data.existingPartner);
        
        // Show partner modal if no existing partner found OR if supplier name needs manual entry
        console.log('📊 Upload response:', {
          existingPartner: data.existingPartner,
          needsManualSupplierEntry: data.needsManualSupplierEntry,
          extractedData: data.extractedData
        });
        
        if (!data.existingPartner || data.needsManualSupplierEntry) {
          console.log('🔄 Showing partner modal');
          // Pre-populate form with extracted data
          // Tax ID: numbers that do NOT start with BG
          // VAT Tax ID: numbers that DO start with BG
          const vatTaxId = data.extractedData?.supplierDetails?.vatTaxId || '';
          const regularTaxId = data.extractedData?.supplierDetails?.taxId || '';
          
          setPartnerFormData({
            name: data.extractedData?.supplierName || '',
            taxId: regularTaxId, // Only non-BG numbers
            vatTaxId: vatTaxId, // Only BG numbers
            address: data.extractedData?.supplierDetails?.address || '',
            city: data.extractedData?.supplierDetails?.city || '',
            country: 'Bulgaria',
            mrp: data.extractedData?.supplierDetails?.contactPerson || '', // Map contactPerson to mrp
            bankName: '',
            swiftBic: '',
            iban: data.extractedData?.supplierDetails?.iban || '',
            email: data.extractedData?.supplierDetails?.email || '',
            phone: data.extractedData?.supplierDetails?.phone || ''
          });
          setShowPartnerModal(true);
        } else {
          console.log('✅ Invoice uploaded successfully');
          setMessage({ type: 'success', text: t('incomingInvoices.invoiceUploadedSuccessfully') });
          fetchPendingInvoices();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || t('incomingInvoices.uploadFailed'));
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setMessage({ type: 'error', text: error.message || t('incomingInvoices.failedToUploadInvoice') });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handlePartnerFormChange = (field, value) => {
    setPartnerFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreatePartner = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      
      // Map frontend fields to backend fields
      const partnerData = {
        name: partnerFormData.name,
        taxId: partnerFormData.taxId,
        vatTaxId: partnerFormData.vatTaxId,
        address: partnerFormData.address,
        city: partnerFormData.city,
        country: partnerFormData.country,
        contactPerson: partnerFormData.mrp, // Map mrp to contactPerson
        email: partnerFormData.email || 'n/a@example.com', // Provide default if empty
        phone: partnerFormData.phone || 'N/A', // Provide default if empty
        bankDetails: {
          bankName: partnerFormData.bankName,
          swiftCode: partnerFormData.swiftBic,
          iban: partnerFormData.iban
        }
      };

      const response = await fetch('/api/finance/partners', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(partnerData)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('incomingInvoices.partnerCreatedSuccessfully') });
        setShowPartnerModal(false);
        // Reset form data
        setPartnerFormData({
          name: '',
          taxId: '',
          vatTaxId: '',
          address: '',
          city: '',
          country: 'Bulgaria',
          mrp: '',
          bankName: '',
          swiftBic: '',
          iban: '',
          email: '',
          phone: ''
        });
        setMessage({ type: 'success', text: t('incomingInvoices.partnerCreatedSuccessfullyInvoicePending') });
        fetchPendingInvoices(); // Refresh pending invoices to show the newly uploaded invoice
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || t('incomingInvoices.failedToCreatePartner'));
      }
    } catch (error) {
      console.error('Error creating partner:', error);
      setMessage({ type: 'error', text: error.message || t('incomingInvoices.failedToCreatePartner') });
    }
  };

  const handleViewDetails = (invoice) => {
    navigate(`/admin/dashboard/incoming-invoices/${invoice._id}`);
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm(t('incomingInvoices.confirmDeleteInvoice'))) {
      return;
    }

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/incoming-invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('incomingInvoices.invoiceDeletedSuccessfully') });
        fetchIncomingInvoices();
      } else {
        throw new Error(t('incomingInvoices.failedToDeleteInvoice'));
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      setMessage({ type: 'error', text: t('incomingInvoices.failedToDeleteInvoice') });
    }
  };

  const handleApproveInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/incoming-invoices/${invoiceId}/approve`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('incomingInvoices.invoiceApprovedSuccessfully') });
        fetchPendingInvoices();
        fetchIncomingInvoices();
      } else {
        throw new Error(t('incomingInvoices.failedToApproveInvoice'));
      }
    } catch (error) {
      console.error('Error approving invoice:', error);
      setMessage({ type: 'error', text: t('incomingInvoices.failedToApproveInvoice') });
    }
  };

  const handleRejectInvoice = async (invoiceId) => {
    const reviewNotes = prompt(t('incomingInvoices.provideRejectionReason'));
    if (!reviewNotes) return;

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`/api/incoming-invoices/${invoiceId}/reject`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reviewNotes })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: t('incomingInvoices.invoiceRejectedSuccessfully') });
        fetchPendingInvoices();
      } else {
        throw new Error(t('incomingInvoices.failedToRejectInvoice'));
      }
    } catch (error) {
      console.error('Error rejecting invoice:', error);
      setMessage({ type: 'error', text: t('incomingInvoices.failedToRejectInvoice') });
    }
  };

  const formatCurrency = (amount, currency = 'BGN') => {
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('bg-BG');
  };

  // Filter invoices based on search and filters (only show approved invoices in the list)
  const filteredInvoices = incomingInvoices.filter(invoice => {
    // Only show approved invoices in the main list
    if (invoice.status !== 'approved') return false;
    
    const matchesSearch = searchTerm === '' || 
      invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.supplierName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPartner = filterPartner === '' || 
      invoice.supplierName === filterPartner;
    
    const matchesPaymentType = filterPaymentType === '' || 
      invoice.paymentType === filterPaymentType;
    
    return matchesSearch && matchesPartner && matchesPaymentType;
  });

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterPartner('');
    setFilterPaymentType('');
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">{t('incomingInvoices.loadingIncomingInvoices')}</p>
        </div>
      </div>
    );
  }

  return (
          <>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-full mx-auto">
                  {/* Hero Section */}
          <div className="mb-6 px-4 sm:px-6 lg:px-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('incomingInvoices.incomingInvoices')}</h1>
            <p className="text-gray-600">{t('incomingInvoices.uploadAndManageIncomingInvoices')}</p>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 mx-4 sm:mx-6 lg:mx-8 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            <span>{message.text}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6 px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('upload')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'upload'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('incomingInvoices.uploadInvoice')}
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t('incomingInvoices.incomingInvoicesList')}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'upload' && (
          <div className="space-y-6">
            {/* Upload Section */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('incomingInvoices.uploadInvoice')}</h3>
              
              {/* Upload Zone */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <div className="text-sm text-gray-600 mb-4">
                  <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500">
                    <span>{t('incomingInvoices.uploadAFile')}</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleFileUpload(file);
                        }
                      }}
                      disabled={uploading}
                    />
                  </label>
                  <p className="pl-1">{t('incomingInvoices.orDragAndDrop')}</p>
                </div>
                <p className="text-xs text-gray-500">{t('incomingInvoices.fileTypesSupported')}</p>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                    <span>{t('incomingInvoices.processingInvoice')}</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Pending Review Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">{t('incomingInvoices.pendingReview')}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {pendingInvoices.length} {pendingInvoices.length === 1 ? t('incomingInvoices.invoice') : t('incomingInvoices.invoices')} {t('incomingInvoices.awaitingReview')}
                </p>
              </div>
              
              {pendingInvoices.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentPlusIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">{t('incomingInvoices.noInvoicesPendingReview')}</p>
                  <p className="text-sm text-gray-500">{t('incomingInvoices.uploadInvoiceToStartReview')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.invoice')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.supplier')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.amount')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.uploaded')}
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pendingInvoices.map((invoice) => (
                        <tr key={invoice._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {invoice.invoiceNumber}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {invoice.supplierName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(invoice.totalAmount, invoice.currency)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(invoice.uploadedAt)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleViewDetails(invoice)}
                                className="text-blue-600 hover:text-blue-900"
                                title={t('incomingInvoices.viewDetails')}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleApproveInvoice(invoice._id)}
                                className="text-green-600 hover:text-green-900"
                                title={t('incomingInvoices.approve')}
                              >
                                <CheckCircleIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleRejectInvoice(invoice._id)}
                                className="text-red-600 hover:text-red-900"
                                title={t('incomingInvoices.reject')}
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'list' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Search */}
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('incomingInvoices.searchInvoices')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Partner Filter */}
                <select
                  value={filterPartner}
                  onChange={(e) => setFilterPartner(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('incomingInvoices.allPartners')}</option>
                  {partners.map((partner) => (
                    <option key={partner._id} value={partner.name}>
                      {partner.name}
                    </option>
                  ))}
                </select>

                {/* Payment Type Filter */}
                <select
                  value={filterPaymentType}
                  onChange={(e) => setFilterPaymentType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('incomingInvoices.allPaymentTypes')}</option>
                  <option value="Cash">{t('incomingInvoices.paymentTypeCash')}</option>
                  <option value="Bank Transfer">{t('incomingInvoices.paymentTypeBankTransfer')}</option>
                  <option value="Unknown">{t('incomingInvoices.paymentTypeUnknown')}</option>
                </select>

                {/* Clear Filters */}
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <FunnelIcon className="h-4 w-4" />
                  <span>{t('incomingInvoices.clearFilters')}</span>
                </button>
              </div>
            </div>

            {/* Incoming Invoices List */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">{t('incomingInvoices.incomingInvoices')}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {filteredInvoices.length} {filteredInvoices.length === 1 ? t('incomingInvoices.incomingInvoice') : t('incomingInvoices.incomingInvoices')} {t('incomingInvoices.found')}
                </p>
              </div>
              
              {filteredInvoices.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentPlusIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    {incomingInvoices.length === 0 ? t('incomingInvoices.noIncomingInvoicesYet') : t('incomingInvoices.noInvoicesMatchFilters')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {incomingInvoices.length === 0 ? t('incomingInvoices.uploadInvoiceToGetStarted') : t('incomingInvoices.tryAdjustingSearchOrFilters')}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.invoice')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.supplier')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.amount')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.issueDate')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.dueDate')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.status')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.paymentType')}
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('incomingInvoices.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredInvoices.map((invoice) => (
                        <tr key={invoice._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {invoice.invoiceNumber}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {invoice.supplierName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(invoice.totalAmount, invoice.currency)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(invoice.issueDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(invoice.dueDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                              invoice.status === 'Pending' ? 'bg-purple-100 text-purple-800' :
                              invoice.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {t(`incomingInvoices.status${invoice.status}`)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {invoice.paymentType || t('incomingInvoices.paymentTypeUnknown')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => handleViewDetails(invoice)}
                                className="text-blue-600 hover:text-blue-900"
                                title={t('incomingInvoices.viewDetails')}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {/* TODO: Edit invoice */}}
                                className="text-green-600 hover:text-green-900"
                                title={t('incomingInvoices.edit')}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteInvoice(invoice._id)}
                                className="text-red-600 hover:text-red-900"
                                title={t('incomingInvoices.delete')}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Partner Creation Modal */}
        {showPartnerModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('incomingInvoices.createNewPartner')}</h3>
                <p className="text-sm text-gray-600 mb-4">
                  {partnerFormData.name 
                    ? t('incomingInvoices.newSupplierDetected')
                    : t('incomingInvoices.supplierNameNotExtracted')
                  }
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Partner Name */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.partnerName')} *</label>
                    <input
                      type="text"
                      value={partnerFormData.name}
                      onChange={(e) => handlePartnerFormChange('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  
                  {/* Tax ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.taxIdIfStartsWithoutBG')}</label>
                    <input
                      type="text"
                      value={partnerFormData.taxId}
                      onChange={(e) => handlePartnerFormChange('taxId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* VAT Tax ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.vatTaxIdIfStartsWithBG')}</label>
                    <input
                      type="text"
                      value={partnerFormData.vatTaxId}
                      onChange={(e) => handlePartnerFormChange('vatTaxId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* Address */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.address')}</label>
                    <input
                      type="text"
                      value={partnerFormData.address}
                      onChange={(e) => handlePartnerFormChange('address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* City */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.city')}</label>
                    <input
                      type="text"
                      value={partnerFormData.city}
                      onChange={(e) => handlePartnerFormChange('city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.country')}</label>
                    <input
                      type="text"
                      value={partnerFormData.country}
                      onChange={(e) => handlePartnerFormChange('country', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* MRP */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.mrp')}</label>
                    <input
                      type="text"
                      value={partnerFormData.mrp}
                      onChange={(e) => handlePartnerFormChange('mrp', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* Bank Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.bankName')}</label>
                    <input
                      type="text"
                      value={partnerFormData.bankName}
                      onChange={(e) => handlePartnerFormChange('bankName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* SWIFT/BIC Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.swiftBicCode')}</label>
                    <input
                      type="text"
                      value={partnerFormData.swiftBic}
                      onChange={(e) => handlePartnerFormChange('swiftBic', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* IBAN */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.iban')}</label>
                    <input
                      type="text"
                      value={partnerFormData.iban}
                      onChange={(e) => handlePartnerFormChange('iban', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.email')}</label>
                    <input
                      type="email"
                      value={partnerFormData.email}
                      onChange={(e) => handlePartnerFormChange('email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('incomingInvoices.phone')}</label>
                    <input
                      type="text"
                      value={partnerFormData.phone}
                      onChange={(e) => handlePartnerFormChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowPartnerModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    {t('incomingInvoices.cancel')}
                  </button>
                  <button
                    onClick={handleCreatePartner}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    {t('incomingInvoices.createPartner')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default IncomingInvoices; 